import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  ZONE_3_FOUL_ACTIONS,
  type LiveActionDefinition,
} from '@/components/live/LiveActionZones';
import { LEFT_ANCHOR_POPOVER_ORIGINS, leftAnchorPopoverPaperSx } from '@/components/review/anchorPopoverSx';

interface Props {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onInsert: (labels: string[]) => void;
  dialogZIndex?: number;
  dark?: boolean;
}

export function appendRemarkQuickInsert(current: string, labels: string[]): string {
  if (labels.length === 0) return current;
  const tags = labels.join('、');
  const base = current.trim();
  return base ? `${base}、${tags}` : tags;
}

export default function RemarkQuickInsertDialog({
  open,
  anchorEl,
  onClose,
  onInsert,
  dialogZIndex,
  dark = false,
}: Props) {
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) setSelectedKeys(new Set());
  }, [open]);

  const selectedActions = useMemo(
    () => ZONE_3_FOUL_ACTIONS.filter((action) => selectedKeys.has(action.key)),
    [selectedKeys]
  );

  const toggleAction = (action: LiveActionDefinition) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(action.key)) next.delete(action.key);
      else next.add(action.key);
      return next;
    });
  };

  const buttonSx = {
    minHeight: 40,
    py: 0.5,
    px: 0.75,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.85rem',
    lineHeight: 1.25,
    borderRadius: 1.25,
    border: '1px solid',
    whiteSpace: 'nowrap',
    ...(dark
      ? {
          color: 'rgba(255,255,255,0.9)',
          borderColor: 'rgba(255,255,255,0.22)',
          bgcolor: 'rgba(255,255,255,0.04)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        }
      : {
          bgcolor: 'background.paper',
          borderColor: 'divider',
          color: 'text.primary',
          '&:hover': { bgcolor: 'action.hover' },
        }),
  } as const;

  return (
    <Popover
      open={open && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      {...LEFT_ANCHOR_POPOVER_ORIGINS}
      disableScrollLock
      marginThreshold={8}
      slotProps={{
        root: dialogZIndex != null ? { sx: { zIndex: dialogZIndex } } : undefined,
        paper: {
          sx: leftAnchorPopoverPaperSx(dark, { xs: 'min(calc(100vw - 32px), 340px)', sm: 420 }),
        },
      }}
    >
      <Stack sx={{ borderRadius: 'inherit' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, pt: 1, pb: 0.75, gap: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800} color={dark ? '#fff' : 'text.primary'}>
              快速插入 · 犯規事件
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={`已選 ${selectedKeys.size}`}
              color={selectedKeys.size > 0 ? 'secondary' : 'default'}
              sx={{
                height: 22,
                fontSize: '0.68rem',
                ...(dark
                  ? { color: 'rgba(255,255,255,0.75)', borderColor: 'rgba(255,255,255,0.28)' }
                  : {}),
              }}
            />
          </Stack>
          <IconButton size="small" aria-label="關閉" onClick={onClose} sx={{ color: dark ? 'rgba(255,255,255,0.7)' : undefined, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Divider sx={{ borderColor: dark ? 'rgba(255,255,255,0.12)' : undefined }} />

        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
            gap: 0.75,
          }}
        >
          {ZONE_3_FOUL_ACTIONS.map((action) => {
            const selected = selectedKeys.has(action.key);
            return (
              <Button
                key={action.key}
                onClick={() => toggleAction(action)}
                variant={selected ? 'contained' : 'outlined'}
                color={selected ? 'secondary' : 'inherit'}
                sx={{
                  ...buttonSx,
                  ...(selected
                    ? {
                        borderColor: 'secondary.main',
                        borderWidth: 2,
                        fontWeight: 800,
                      }
                    : {}),
                }}
              >
                {action.label}
              </Button>
            );
          })}
        </Box>

        <Divider sx={{ borderColor: dark ? 'rgba(255,255,255,0.12)' : undefined }} />

        <Stack direction="row" spacing={1} sx={{ px: 1.5, py: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            size="small"
            sx={dark ? { color: 'rgba(255,255,255,0.88)', borderColor: 'rgba(255,255,255,0.28)' } : undefined}
          >
            取消
          </Button>
          <Button
            onClick={() => onInsert(selectedActions.map((a) => a.label))}
            variant="contained"
            color="secondary"
            fullWidth
            size="small"
            disabled={selectedActions.length === 0}
          >
            插入 ({selectedActions.length})
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
